// How today's package files, address parameters and browser storage read as settings (README "Legacy readers").
// Everything here mirrors main 23dc2a0: main.js `state` and the aurora defaults, config.js applySeason and
// seasonMerge, trees.js createTreeLayer, render.js createPipeline, ui.js `hide()` and the explore fold.
import { CONTROLS, NO_BUTTON } from './controls.js';
import { REGISTRY } from './registry.js';
import { legacyTour, LEGACY_TOUR_ID } from './guided-tour.js';
import { isObj, at } from './values.js';
/** @typedef {import('./typedefs.js').SettingEntry} SettingEntry */
/** @typedef {import('./typedefs.js').Preset} Preset */

const pad = (n) => String(n).padStart(2, '0');
const vec3 = (a) => Array.isArray(a) && a.length === 3 && a.every(Number.isFinite);
const SNOW_LEVELS = [{ id: 'none', load: 0 }, { id: 'light', load: 0.25 }, { id: 'medium', load: 0.45 }, { id: 'heavy', load: 0.65 }]; // trees.js
const snowLevels = (resort) => { const l = at(resort, 'vegetation.snow.levels'); return Array.isArray(l) && l.length ? l : SNOW_LEVELS; };
const hasTour = (v) => Array.isArray(v) && v.some((k) => typeof k === 'string' && k);

// (value at `legacy.path`, resort.json, content.json) -> the setting's value; undefined = the package says nothing
const READ = {
  monthDay: (v) => (isObj(v) ? `${pad(v.month ?? 12)}-${pad(v.day ?? 21)}` : undefined),
  readySeasons: (v) => (Array.isArray(v) ? v.filter((s) => s?.ready).map((s) => s.id) : undefined),
  auroraOn: (v, r) => (v === false || at(r, 'features.aurora') === false ? false : undefined),
  auroraAuto: (v) => (isObj(v) && v.default === true ? true : undefined), // aurora.js: the display plays from the start only with `default: true`
  snowLevel: (v, r) => {
    const trees = at(r, 'vegetation.trees'), levels = snowLevels(r);
    const load = trees?.snow === false ? 0 : (trees?.snowLoad ?? at(r, 'vegetation.snowLoad') ?? 0.25);
    return (levels.find((l) => l.id === v) || levels.reduce((b, l) => (Math.abs(l.load - load) < Math.abs(b.load - load) ? l : b), levels[0])).id;
  },
  homeView: (v) => (vec3(v?.target) && vec3(v?.offset) ? { position: v.target.map((x, i) => x + v.offset[i]), target: [...v.target], fov: 36 } : undefined),
  legacyTourIds: (v) => (hasTour(v) ? [LEGACY_TOUR_ID] : undefined),
  legacyTour: (v) => (hasTour(v) ? { [LEGACY_TOUR_ID]: legacyTour(v) } : undefined),
  bookingLinks: (c) => {
    const links = [c?.unitTypes, c?.places].flatMap((g) => Object.entries(isObj(g) ? g : {})).filter(([, v]) => typeof v?.link === 'string' && v.link);
    return links.length ? { schema: 1, targets: Object.fromEntries(links.map(([k, v]) => [k, { fallbackUrl: v.link }])) } : undefined;
  },
  fixedOffset: (v) => (Number.isFinite(v) ? `${v < 0 ? '-' : '+'}${pad(Math.trunc(Math.abs(v)))}:${pad(Math.round((Math.abs(v) % 1) * 60))}` : undefined),
  // VIS-136: resort.json `site.tz` (an IANA zone name such as `Europe/Helsinki`, or `+02:00`) when set, else site.utcOffset as a fixed offset
  siteZone: (v, r) => { const tz = at(r, 'site.tz'); return typeof tz === 'string' && tz.trim() ? tz.trim() : READ.fixedOffset(v); },
};

/**
 * The value the package gives a setting (layer 2 of `resolve`); undefined when it says nothing.
 * @param {SettingEntry} e
 * @param {object} [resort]   resort.json
 * @param {object} [content]  content.json
 */
export function readPackage(e, resort, content) {
  const l = e.legacy;
  if (!l || typeof l.path !== 'string') return undefined;
  const raw = at(l.file === 'content' ? content : resort, l.path);
  return l.read ? READ[l.read]?.(raw, resort, content) : raw;
}

// config.js seasonMerge without changing its input: objects merge, lists of { id | key } items merge item by item
// (`_drop: true` removes one), anything else is replaced. Only the patched branches are copied.
const idOf = (v) => (v && typeof v === 'object' ? v.id ?? v.key ?? null : null);
export function seasonMerged(cur, patch) {
  if (Array.isArray(patch) && Array.isArray(cur) && patch.length && patch.every((x) => idOf(x) !== null) && cur.some((x) => idOf(x) !== null)) {
    const out = [...cur];
    for (const item of patch) {
      const i = out.findIndex((x) => idOf(x) === idOf(item));
      if (item._drop) { if (i >= 0) out.splice(i, 1); } else if (i >= 0) out[i] = seasonMerged(out[i], item); else out.push(item);
    }
    return out;
  }
  if (!isObj(patch) || !isObj(cur)) return patch;
  const out = { ...cur };
  for (const [k, v] of Object.entries(patch)) if (k !== '__proto__') out[k] = seasonMerged(cur[k], v);
  return out;
}

/**
 * One of today's browser-storage values as a setting's value (layer 7b under an implicit legacy preset), in
 * today's format: a JSON object with `legacy.storeField`, `"1"` for a switch, or the value as text.
 * @param {SettingEntry} e
 * @param {string} raw
 * @param {object} [resort]
 */
export function readStore(e, raw, resort) {
  const field = e.legacy.storeField;
  if (!field) return e.type === 'bool' ? raw === '1' : raw;
  let saved;
  try { saved = JSON.parse(raw); } catch { return undefined; }
  if (!isObj(saved)) return undefined;
  return saved[field];
}

const LEGACY_IDS = ['legacy', 'legacy-embed', 'legacy-kiosk'];
export const isLegacy = (preset) => LEGACY_IDS.includes(preset?.id);

/**
 * The implicit preset of a page opened without `?preset` (contract section 3): the controls today's `resort.json`
 * flags leave on the page, minus what `?embed` / `?kiosk` hide, and the values those two modes set. Read from each
 * control's and setting's `legacy`; this is the only place where "show what today's config shows" is allowed.
 * @param {object} resort  resort.json as loaded
 * @param {'embed' | 'kiosk' | { embed?: boolean, kiosk?: boolean } | null} [mode]
 * @returns {Preset}
 */
export function legacyPreset(resort, mode) {
  const modes = ['embed', 'kiosk'].filter((m) => (isObj(mode) ? mode[m] : mode === m));
  const pass = (c) => {
    const v = at(resort, c.path);
    return 'not' in c ? v !== c.not : 'is' in c ? v === c.is : Array.isArray(v) ? v.length > 0 : !!v;
  };
  const show = CONTROLS.filter(({ id, legacy: l }) => l && !NO_BUTTON.includes(id) && l.when.every(pass) // no guest button (owner, 2026-10-28)
    && !modes.some((m) => l.modes[m] === false || (l.modeFlags?.[m] && at(resort, l.modeFlags[m]) === false))).map((c) => c.id);
  const values = {};
  for (const e of REGISTRY) for (const m of modes) if (e.legacy?.modes && m in e.legacy.modes) values[e.key] = e.legacy.modes[m];
  const id = modes.includes('kiosk') ? 'legacy-kiosk' : modes.includes('embed') ? 'legacy-embed' : 'legacy';
  return { schema: 1, id, slug: id, name: { en: 'Default view' }, access: 'public', enabled: true, values, show };
}
